import { CheckCircle2, ClipboardCheck, RotateCcw, ShieldAlert, CalendarClock } from "lucide-react";
import { StatusBadge, fmtDate } from "../lib/status";

/**
 * The staff-facing return checkpoint shared by the real Booking Detail page
 * and the sandboxed public walkthrough.  It contains no booking logic: the
 * caller supplies the canonical complete/inspection actions.  That keeps the
 * UI honest while the server remains the only source of lifecycle truth.
 */
export default function ReturnReadinessPanel({
  booking,
  returnInspection,
  nextBooking,
  onRecordReturn,
  onOpenInspection,
  onOpenNextBooking,
  busy = false,
  compact = false,
}) {
  if (!booking) return null;
  const vehicle = booking.vehicle || {};
  const completed = booking.status === "completed";
  const inspectionDone = returnInspection?.status === "finalized";
  const needsInspection = completed && vehicle.status === "returned_needs_check";
  const blocked = completed && ["damaged", "maintenance", "unavailable", "retired"].includes(vehicle.status);
  const available = completed && vehicle.status === "available" && inspectionDone;

  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${compact ? "p-3" : "p-5"}`}
             data-testid="return-readiness-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Return &amp; vehicle readiness</div>
          <h2 className={`font-display font-semibold text-slate-950 ${compact ? "text-base mt-0.5" : "text-lg mt-1"}`}>
            {available ? "Vehicle cleared for its next booking"
              : blocked ? "Vehicle remains blocked after return"
              : needsInspection ? "Return recorded: inspection required"
              : completed ? "Complete the return inspection"
              : "Record the return, then inspect the vehicle"}
          </h2>
        </div>
        {vehicle.status && <StatusBadge status={vehicle.status} type="vehicle" data-testid="return-readiness-status" />}
      </div>

      <ol className={`grid gap-2 ${compact ? "mt-3" : "mt-4 sm:grid-cols-3"}`} aria-label="Vehicle return progress">
        <ProgressStep icon={RotateCcw} label="1. Record return" done={completed} active={!completed} />
        <ProgressStep icon={ClipboardCheck} label="2. Inspect vehicle" done={inspectionDone} active={completed && !inspectionDone} />
        <ProgressStep icon={available ? CheckCircle2 : ShieldAlert} label="3. Readiness decided"
                      done={available || blocked} active={inspectionDone && !available && !blocked} danger={blocked} />
      </ol>

      {!completed && (
        <div className="mt-4">
          <p className="text-sm text-slate-600">Closing the ongoing rental records its return and automatically places the vehicle in <b>Needs Inspection</b>. It is not available to new reservations yet.</p>
          <button type="button" onClick={onRecordReturn} disabled={busy || booking.status !== "ongoing"}
                  data-testid="return-record-btn"
                  className="mt-3 w-full sm:w-auto rounded-lg bg-[#1B4332] px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
            <RotateCcw className="mr-1.5 inline h-4 w-4" /> {busy ? "Recording…" : "Record vehicle returned"}
          </button>
          {booking.status !== "ongoing" && <p className="mt-2 text-xs text-amber-700">Pickup handover must put this booking Ongoing before a return can be recorded.</p>}
        </div>
      )}

      {completed && !inspectionDone && (
        <div className="mt-4">
          <p className="text-sm text-slate-600">Check odometer, fuel or charge, cleaning, exterior/interior condition, photos, and customer acknowledgement. Finalizing this check updates mileage and decides vehicle readiness.</p>
          <button type="button" onClick={onOpenInspection} data-testid="return-inspection-start-btn"
                  className="mt-3 w-full sm:w-auto rounded-lg bg-[#1B4332] px-4 py-2.5 text-sm font-semibold text-white">
            <ClipboardCheck className="mr-1.5 inline h-4 w-4" /> {returnInspection ? "Continue return inspection" : "Start return inspection"}
          </button>
        </div>
      )}

      {inspectionDone && (
        <div className={`mt-4 rounded-lg border p-3 text-sm ${blocked ? "border-amber-300 bg-amber-50 text-amber-950" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}>
          <div className="font-semibold inline-flex items-center gap-2">
            {blocked ? <ShieldAlert className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {blocked ? "Inspection saved; this vehicle is not available." : "Inspection finalized; this vehicle is Available."}
          </div>
          {nextBooking ? (
            <div className="mt-2 flex items-start gap-2" data-testid="return-next-booking">
              <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Next booking <b>{nextBooking.booking_reference}</b> remains assigned to this vehicle
                {nextBooking.start_datetime ? ` for ${fmtDate(nextBooking.start_datetime)}` : ""}.
                {blocked ? " Resolve the vehicle issue or reassign that booking before pickup." : " It can proceed once its pickup time arrives."}
              </span>
            </div>
          ) : (
            <p className="mt-1.5">{blocked ? "Resolve the incident or readiness issue before assigning another rental." : "The vehicle can now be used by the next eligible booking."}</p>
          )}
          <button type="button" onClick={onOpenInspection} className="mt-2 text-xs font-semibold underline underline-offset-2" data-testid="return-inspection-view-btn">
            View finalized return inspection
          </button>
          {nextBooking && onOpenNextBooking && (
            <button type="button" onClick={onOpenNextBooking} data-testid="return-open-next-booking-btn"
                    className="mt-3 ml-3 rounded-lg bg-[#1B4332] px-3 py-2 text-xs font-semibold text-white">
              Open next booking
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function ProgressStep({ icon: Icon, label, done, active, danger }) {
  return (
    <li className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium ${
      danger ? "border-amber-300 bg-amber-50 text-amber-900"
        : done ? "border-emerald-200 bg-emerald-50 text-emerald-800"
        : active ? "border-sky-200 bg-sky-50 text-sky-800"
        : "border-slate-200 bg-slate-50 text-slate-500"
    }`}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> {label}
    </li>
  );
}
